// src/infrastructure/http/errorDetail.ts
import type { AxiosError } from 'axios'
import type { ReassignRequired } from '@/types/invoice_purchase_order_association'
import { readReassignRequired } from '@/utils/invoicePoReassignError'

/**
 * The backend's own `detail` message from a failed request, or `null`.
 *
 * Every action in the legacy drawer shows `response?.data?.detail || <generic>` — the backend's
 * sentence when it sends one, a generic only as a fallback. That matters because the specific message
 * is usually the actionable one ("la factura ya está creada en el ERP", "falta la categoría"), and
 * replacing it with a generic throws away the only thing that tells the user what to do.
 *
 * `HttpClient` rethrows the raw axios error, so this reads it back at the call site. A fuller answer
 * would be typed domain errors per action; this is the smaller step that preserves the message today
 * without inventing an error taxonomy the backend does not have.
 */
export function backendDetail(e: unknown): string | null {
  const detail = ((e as AxiosError)?.response?.data as { detail?: unknown } | undefined)?.detail
  return typeof detail === 'string' && detail.trim() ? detail : null
}

/**
 * The backend's actionable `error_details` lines from a failed request (e.g. the ERP envelope's
 * per-line errors), or `[]`. Companion to `backendDetail`: `detail` is the generic sentence,
 * `error_details` is the list the ERP-error modal renders.
 */
export function backendErrorDetails(e: unknown): string[] {
  const details = ((e as AxiosError)?.response?.data as { error_details?: unknown } | undefined)?.error_details
  return Array.isArray(details) ? details.filter((d): d is string => typeof d === 'string') : []
}

/**
 * The backend's `error_code` from a failed request, or `null`. Lets a caller branch on a specific
 * failure (e.g. `VALIDATION_STILL_FAILS`) instead of matching the human `detail` string.
 */
export function backendErrorCode(e: unknown): string | null {
  const code = ((e as AxiosError)?.response?.data as { error_code?: unknown } | undefined)?.error_code
  return typeof code === 'string' && code ? code : null
}

/** A string field from the backend error's `extra` bag (e.g. `node_title`), or `null`. */
export function backendExtra(e: unknown, key: string): string | null {
  const extra = ((e as AxiosError)?.response?.data as { extra?: Record<string, unknown> } | undefined)?.extra
  const value = extra?.[key]
  return typeof value === 'string' && value ? value : null
}

export function backendReassign(e: unknown): ReassignRequired | null {
  return readReassignRequired((e as AxiosError)?.response as Parameters<typeof readReassignRequired>[0])
}

/** The failed request's HTTP status, or `undefined`. Feeds status-based checks like `isStaleFirewallNodeError`. */
export function backendStatus(e: unknown): number | undefined {
  return (e as AxiosError)?.response?.status
}

/** The failed request's whole response body, for callers that read more than one field of a structured refusal. */
export function backendBody(e: unknown): unknown {
  return (e as AxiosError)?.response?.data
}
